import React from 'react';
import PropTypes from 'prop-types';

import { Flexer } from '@ontruck/orc';

import { withFormikField } from 'utils/formik';
import { injectIntl } from 'react-intl';
import styled from 'styled-components';

import {
  SHIPMENTS_FIELD,
  DAY_HOLIDAY_EXTRA,
  PRICE_INCREMENT_EXTRA,
  DISCOUNT_EXTRA,
  STANDSTILL_EXTRA,
  MANAGEMENT_FEE_EXTRA,
  MANUAL_EXTRA,
  TENDER_EXTRA,
  NEGOTIATED_EXTRA,
  CANCELLATION_EXTRA,
  CANCELLED,
  RETURN_EXTRA,
} from 'modules/shipper-order/Form/constants';

import {
  BUNKER_ADJUSTMENT_FACTOR_EXTRA,
  INCREMENT_FOR_WEIGHT_OVERLOAD_EXTRA,
  INCREMENT_FOR_USING_WEIGHING_SCALE_EXTRA,
} from 'modules/shipper-order/Form/Intraser/tenantConstants';

import {
  StyledExtrasHeader,
  StyledExtrasHeaderColumn,
  StyledFirstColumn,
  StyledExtrasGroup,
  StyledExtrasTitle,
  ExtraElement,
  StandstillExtra,
  InsuranceExtra,
  ModifiedPriceExtra,
  ReturnExtra,
} from 'modules/Common/form/extras';
import { IS_TENANT } from 'tenant/config';

const ExtraElementContainer = styled.div`
  background: #fff;
  > div > div > div + div {
    margin: 0 -16px 0 auto;
  }
`;

const ExtrasSection = ({
  form,
  currency,
  activeExtras,
  disabledExtras,
  quote = {},
  cancellationValues = [],
  extrasAmountsField = 'extra_prices',
  intl,
}) => {
  const commonProps = {
    currency,
    quote,
    extrasAmountsField,
  };
  const labels = intl.messages.modules.shipper_order.form.extra_section;
  const t = {
    base_price: labels.base_price,
    concept: labels.concept,
    charged: labels.charged,
    title_extras: labels.extras,
    [DAY_HOLIDAY_EXTRA]: labels.DAY_HOLIDAY_EXTRA,
    [PRICE_INCREMENT_EXTRA]: labels.PRICE_INCREMENT_EXTRA,
    [DISCOUNT_EXTRA]: labels.DISCOUNT_EXTRA,
    [STANDSTILL_EXTRA]: labels.STANDSTILL_EXTRA,
    [MANAGEMENT_FEE_EXTRA]: labels.MANAGEMENT_FEE_EXTRA,
    [MANUAL_EXTRA]: labels.MANUAL_EXTRA,
    [TENDER_EXTRA]: labels.TENDER_EXTRA,
    [CANCELLATION_EXTRA]: labels.CANCELLATION_EXTRA,
    [NEGOTIATED_EXTRA]: labels.NEGOTIATED_EXTRA,
    [BUNKER_ADJUSTMENT_FACTOR_EXTRA]: labels.BUNKER_ADJUSTMENT_FACTOR_EXTRA,
    [INCREMENT_FOR_USING_WEIGHING_SCALE_EXTRA]: labels.INCREMENT_FOR_USING_WEIGHING_SCALE_EXTRA,
    [INCREMENT_FOR_WEIGHT_OVERLOAD_EXTRA]: labels.INCREMENT_FOR_WEIGHT_OVERLOAD_EXTRA,
  };
  const { values } = form;
  const { status, stops, id: shipperOrderId } = values;

  const showDiscount = activeExtras.includes(DISCOUNT_EXTRA);
  const showStandstill = activeExtras.includes(STANDSTILL_EXTRA);
  const showManagementFee = activeExtras.includes(MANAGEMENT_FEE_EXTRA);
  const showHolidays = activeExtras.includes(DAY_HOLIDAY_EXTRA);
  const showCancellation = status === CANCELLED;
  const showReturn = activeExtras.includes(RETURN_EXTRA);
  const showIncrementForOverload = activeExtras.includes(INCREMENT_FOR_WEIGHT_OVERLOAD_EXTRA);
  const showIncrementForUsingWeighingScale = activeExtras.includes(
    INCREMENT_FOR_USING_WEIGHING_SCALE_EXTRA
  );

  return (
    <div>
      <StyledExtrasGroup>
        <StyledExtrasHeader>
          <StyledExtrasHeaderColumn position="left">{t.concept}</StyledExtrasHeaderColumn>
          <StyledExtrasHeaderColumn position="right">{t.charged}</StyledExtrasHeaderColumn>
        </StyledExtrasHeader>
        <ModifiedPriceExtra quote={quote} currency={currency} isLast={!showReturn && !IS_TENANT} />
        {showReturn && !IS_TENANT && (
          <ReturnExtra
            currency={currency}
            disabled={disabledExtras.includes(RETURN_EXTRA)}
            isLast
          />
        )}
        {showIncrementForOverload && (
          <ExtraElementContainer>
            <ExtraElement
              className="tenant-extra"
              label={t[INCREMENT_FOR_WEIGHT_OVERLOAD_EXTRA]}
              extraName={INCREMENT_FOR_WEIGHT_OVERLOAD_EXTRA}
              {...commonProps}
            />
          </ExtraElementContainer>
        )}
        {showIncrementForUsingWeighingScale && (
          <ExtraElementContainer>
            <ExtraElement
              className="tenant-extra"
              label={t[INCREMENT_FOR_USING_WEIGHING_SCALE_EXTRA]}
              extraName={INCREMENT_FOR_USING_WEIGHING_SCALE_EXTRA}
              isLast
              {...commonProps}
            />
          </ExtraElementContainer>
        )}
      </StyledExtrasGroup>

      {(!IS_TENANT || (showStandstill && stops?.length > 0)) && (
        <>
          <StyledExtrasTitle>{t.title_extras}</StyledExtrasTitle>
          <StyledExtrasGroup>
            <StyledExtrasHeader>
              <StyledFirstColumn p="XS" pLeft="S" pRight="S">
                {t.concept}
              </StyledFirstColumn>
              <Flexer.Child>{t.charged}</Flexer.Child>
            </StyledExtrasHeader>
            {showDiscount && !IS_TENANT && (
              <ExtraElement
                label={t[DISCOUNT_EXTRA]}
                extraName={DISCOUNT_EXTRA}
                isNegative
                {...commonProps}
              />
            )}
            {showStandstill && (
              <StandstillExtra
                stops={stops}
                currency={currency}
                quote={quote}
                extrasAmountsField={extrasAmountsField}
              />
            )}
            {showManagementFee && !IS_TENANT && (
              <ExtraElement
                label={t[MANAGEMENT_FEE_EXTRA]}
                extraName={MANAGEMENT_FEE_EXTRA}
                showExtraComment
                {...commonProps}
              />
            )}
            {showCancellation && !IS_TENANT && (
              <ExtraElement
                label={t[CANCELLATION_EXTRA]}
                extraName={CANCELLATION_EXTRA}
                extraType="PERCENT"
                disabled
                extraValues={cancellationValues}
                isNegative
                showAmount
                referral={shipperOrderId}
                {...commonProps}
              />
            )}
            {!IS_TENANT && (
              <InsuranceExtra
                disabled={(values[SHIPMENTS_FIELD] || []).length > 1}
                currency={currency}
              />
            )}
            {showHolidays && !IS_TENANT && (
              <ExtraElement
                label={t[DAY_HOLIDAY_EXTRA]}
                extraName={DAY_HOLIDAY_EXTRA}
                extraType="PERCENT"
                showAmount
                isLast
                {...commonProps}
              />
            )}
          </StyledExtrasGroup>
        </>
      )}
    </div>
  );
};

ExtrasSection.propTypes = {
  form: PropTypes.object.isRequired,
  currency: PropTypes.string.isRequired,
  activeExtras: PropTypes.array,
  cancellationValues: PropTypes.array,
  quote: PropTypes.object,
  extrasAmountsField: PropTypes.oneOf(['extra_costs', 'extra_prices']),
};

export default withFormikField(injectIntl(ExtrasSection));
